/**
 * Provider registry — the single store of registered AI provider adapters.
 *
 * This file owns registration only. Selection, routing, and fallback policy
 * live in `providerManager.ts`; health tracking lives in `health.ts`.
 *
 * Nothing is registered by default: until a vendor adapter ships, every
 * `aiService.*` call fails with `AIConfigurationError`, exactly as before.
 */

import type { AIProvider } from './AIProvider';
import type { AICapability, AIProviderName } from './types';

const registry = new Map<AIProviderName, AIProvider>();

export type ProviderRegistrationListener = (
  event: 'registered' | 'unregistered' | 'reset',
  provider?: AIProvider,
) => void;

const listeners = new Set<ProviderRegistrationListener>();

function emit(event: 'registered' | 'unregistered' | 'reset', provider?: AIProvider): void {
  listeners.forEach((listener) => listener(event, provider));
}

/** Subscribe to registry mutations. Returns an unsubscribe function. */
export function onProviderRegistryChange(listener: ProviderRegistrationListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function registerProvider(provider: AIProvider): void {
  registry.set(provider.name, provider);
  emit('registered', provider);
}

export function unregisterProvider(name: AIProviderName): void {
  const provider = registry.get(name);
  if (registry.delete(name)) emit('unregistered', provider);
}

export function getProvider(name: AIProviderName): AIProvider | undefined {
  return registry.get(name);
}

export function hasProvider(name: AIProviderName): boolean {
  return registry.has(name);
}

export function listProviders(): AIProvider[] {
  return Array.from(registry.values());
}

export function listProviderNames(): AIProviderName[] {
  return Array.from(registry.keys());
}

/** Registered providers that are configured and advertise the capability. */
export function findProvidersFor(capability: AICapability): AIProvider[] {
  return listProviders().filter((p) => p.supports(capability) && p.isConfigured());
}

/** Test helper — clears all registrations. */
export function resetProviders(): void {
  registry.clear();
  emit('reset');
}
